import { useEffect, useRef, useState } from 'react'
import { IdeaShuffle } from '../components/CreateWithAiModal/IdeaShuffle'
import { PromptComposer } from '../components/CreateWithAiModal/PromptComposer'
import { MAX_PROMPT, MIN_PROMPT, PROMPT_TEMPLATES } from '../components/CreateWithAiModal/promptConfig'
import { useI18n } from '../lib/i18n'
import { editorHref } from '../lib/editorSession'
import { industryIdeas } from '../lib/promptIdeas'
import { savePendingPrompt } from '../lib/pendingPrompt'
import type { WelcomeAnswers } from '../lib/welcomeAnswers'

/**
 * La caja de «Cuéntanos qué quieres construir», para incrustarla en
 * veltica.com con un Embed (`<iframe src="…/embed/prompt">`).
 *
 * Es la misma caja que la pantalla de empezar del panel (`PromptComposer`),
 * sin lo que es de una cuenta: ni fotos ni el contador de generaciones. No
 * llama a la IA: guarda lo escrito (`pendingPrompt`) y lleva a registrarse,
 * y el asistente genera al entrar, ya con cuenta y desde el servidor. Así no
 * hay ninguna clave en la página ni nadie gasta IA sin cuenta.
 *
 * No es un bloque del editor: vive en el panel, así que no la ve ningún
 * cliente como opción.
 */
export function EmbedPromptPage({ hasSession }: { hasSession: boolean }) {
  const { t } = useI18n()
  const [prompt, setPrompt] = useState('')
  const [brief, setBrief] = useState<WelcomeAnswers | null>(null)
  const [revealKey, setRevealKey] = useState(0)
  const [failed, setFailed] = useState(false)
  const fieldRef = useRef<HTMLTextAreaElement>(null)
  const ideas = industryIdeas(brief?.business) ?? PROMPT_TEMPLATES

  /* Transparente: se ve sobre el fondo de la sección de veltica.com. */
  useEffect(() => {
    document.documentElement.classList.add('is-embed-prompt')
    return () => document.documentElement.classList.remove('is-embed-prompt')
  }, [])

  /*
   * Claro u oscuro según el fondo de detrás, para que las letras se lean. Lo
   * dice `?tone=dark` en el código del Embed (una sección oscura), y en el modo
   * diseño de veltica.com lo dice el editor al cambiar de combinación
   * (`veltica:embed-tone`, ver `toneBehind` en `wizardPreviewFrame`): la caja
   * no puede ver el color de la página de fuera.
   */
  useEffect(() => {
    const apply = (tone: unknown) => {
      if (tone === 'dark' || tone === 'light') document.documentElement.setAttribute('data-theme', tone)
    }
    /* Al fotograma siguiente: el tema del panel (`applyTheme` en `App`) se pone después de este efecto y lo pisaría. */
    const fromQuery = new URLSearchParams(window.location.search).get('tone')
    const frame = window.requestAnimationFrame(() => apply(fromQuery))
    const editorOrigin = new URL(editorHref('/')).origin
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; tone?: unknown } | null
      if (event.origin === editorOrigin && data?.type === 'veltica:embed-tone') apply(data.tone)
    }
    window.addEventListener('message', onMessage)
    return () => { window.cancelAnimationFrame(frame); window.removeEventListener('message', onMessage) }
  }, [])

  /*
   * Con `?start=…`: lo escrito en la caja de veltica.com, que allí va dibujada
   * en la página y no en este iframe (ver `promptBox` en el worker). Aquí sólo
   * se guarda y se sigue: a registrarse, o al panel si ya hay sesión.
   */
  const started = useRef(false)
  useEffect(() => {
    const start = new URLSearchParams(window.location.search).get('start')?.trim()
    if (!start || started.current) return
    started.current = true
    if (start.length >= MIN_PROMPT) savePendingPrompt(start.slice(0, MAX_PROMPT), null)
    window.location.replace(hasSession ? '/' : '/signup')
  }, [hasSession])

  function showExample(text: string) {
    setPrompt(text)
    setRevealKey((key) => key + 1)
  }

  function submit() {
    if (prompt.trim().length < MIN_PROMPT) return
    if (!savePendingPrompt(prompt.trim(), brief)) { setFailed(true); return }
    /* En la pestaña entera: el registro no cabe dentro de la caja. */
    const destination = new URL(hasSession ? '/' : '/signup', window.location.origin).toString()
    try {
      if (window.top && window.top !== window) window.top.location.href = destination
      else window.location.assign(destination)
    } catch {
      window.open(destination, '_blank', 'noopener')
    }
  }

  return (
    <main className="dash-start-screen embed-prompt">
      {/* Sólo la caja: sin mascota ni título encima. Va en secciones de
          veltica.com que ya traen su titular —a menudo sobre un fondo oscuro o
          una foto—, y la cabecera ocupaba media altura. El título vive dentro,
          como texto de ayuda, y los ejemplos son la primera pastilla de abajo. */}
      <section className="dash-start-ai" aria-label={t('cwa.title')}>
        <PromptComposer
          value={prompt}
          fieldRef={fieldRef}
          ariaLabel={t('cwa.title')}
          placeholder={t('embedPrompt.placeholder')}
          leading={<IdeaShuffle ideas={ideas} onIdea={showExample} inBox />}
          onChange={setPrompt}
          revealKey={revealKey}
          onSubmit={submit}
          brief={brief}
          onBrief={setBrief}
          note={failed ? t('embedPrompt.saveFailed') : t('embedPrompt.note')}
        />
      </section>
    </main>
  )
}
